import { format } from 'date-fns';
import type { jsPDF as JsPDFClass } from 'jspdf';
import type { autoTable as AutoTableFn, CellHookData } from 'jspdf-autotable';

import {
  RGB, cellRgb, formatBound, interpolateStops, legendTicks,
} from './heatmapColorScale';
import { HeatmapMatrix } from './heatmapMatrixFromOption';

// PDF do heatmap da Visão Detalhada do Acompanhamento de Previsão (#2009, item 8).
// A imagem baixada saía só com o pedaço visível da tabela (~13 cidades); o PDF traz a
// tabela inteira, quebrada em páginas, com os mesmos valores e cores da tela.

type JsPDFConstructor = typeof JsPDFClass;
type JsPDF = InstanceType<JsPDFConstructor>;

const MARGIN = 10;
const HEADER_HEIGHT = 22;
const FOOTER_HEIGHT = 24;
const FONT_SIZE = 7;
const CELL_PADDING = 1.2;
const MIN_DATE_COLUMN = 10;
const MAX_DATE_COLUMN = 22;
const LEGEND_WIDTH = 70;
const LEGEND_STEPS = 70;
const LEGEND_TICK_COUNT = 5;

const DARK_BLUE: RGB = [33, 47, 88];
const MEDIUM_BLUE: RGB = [38, 84, 138];
const GRAY_TEXT: RGB = [88, 88, 88];
const NO_DATA_HATCH: RGB = [233, 233, 233];
const WHITE: RGB = [255, 255, 255];

export type HeatmapPdfCellStyle = { noData: true } | { noData: false; fill: RGB; text: RGB };

export interface HeatmapPdfTable {
  head: string[][];
  body: string[][];
  /** Estilo da célula de dado (sem a coluna das cidades): linha e coluna da matriz. */
  cellStyle: (row: number, column: number) => HeatmapPdfCellStyle;
}

export function buildHeatmapPdfTable(matrix: HeatmapMatrix): HeatmapPdfTable {
  const {
    dates, rows, cells, min, max, colorStops,
  } = matrix;

  return {
    head: [['', ...dates]],
    body: rows.map((label, r) => [
      label,
      ...cells[r].map((value) => (value === null ? '' : value.toFixed(1))),
    ]),
    cellStyle: (row, column) => {
      const value = cells[row]?.[column];
      if (value === null || value === undefined) return { noData: true };
      const { background, text } = cellRgb(value, min, max, colorStops);
      return { noData: false, fill: background, text };
    },
  };
}

// Hachura diagonal das células sem dado, como o `hatch-nodata` da tabela da tela.
function hatch(doc: JsPDF, x: number, y: number, width: number, height: number) {
  const step = 1.2;
  doc.setDrawColor(...NO_DATA_HATCH);
  doc.setLineWidth(0.35);
  for (let offset = -height; offset < width; offset += step) {
    let x0 = x + offset;
    let y0 = y + height;
    let x1 = x0 + height;
    let y1 = y;
    if (x0 < x) { y0 -= x - x0; x0 = x; }
    if (x1 > x + width) { y1 += x1 - (x + width); x1 = x + width; }
    if (x0 < x1) doc.line(x0, y0, x1, y1);
  }
}

function drawHeader(doc: JsPDF, matrix: HeatmapMatrix, pageWidth: number) {
  let y = MARGIN + 3;
  if (matrix.title) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...DARK_BLUE);
    doc.text(matrix.title, MARGIN, y);
    y += 6;
  }
  if (matrix.subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_TEXT);
    doc.text(doc.splitTextToSize(matrix.subtitle, pageWidth - 2 * MARGIN), MARGIN, y);
  }
}

function drawLegend(doc: JsPDF, matrix: HeatmapMatrix, pageHeight: number) {
  const { min, max, colorStops } = matrix;
  const top = pageHeight - FOOTER_HEIGHT + 5;
  const barHeight = 3;
  const stepWidth = LEGEND_WIDTH / LEGEND_STEPS;

  // A barra da tela é um linear-gradient dos colorStops; os ticks ficam na régua sqrt.
  for (let i = 0; i < LEGEND_STEPS; i += 1) {
    doc.setFillColor(...interpolateStops(colorStops, i / (LEGEND_STEPS - 1)));
    // Sobreposição de 0,3 mm entre os degraus: sem ela o leitor de PDF mostra riscos brancos.
    doc.rect(MARGIN + i * stepWidth, top, stepWidth + 0.3, barHeight, 'F');
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SIZE);
  doc.setTextColor(...GRAY_TEXT);
  const ticks = legendTicks(min, max, LEGEND_TICK_COUNT);
  ticks.forEach((tick, i) => {
    const p = ticks.length > 1 ? i / (ticks.length - 1) : 0;
    doc.text(formatBound(tick), MARGIN + p * LEGEND_WIDTH, top + barHeight + 3.5, { align: 'center' });
  });

  const swatchX = MARGIN + LEGEND_WIDTH + 10;
  doc.setDrawColor(...NO_DATA_HATCH);
  doc.setLineWidth(0.2);
  doc.rect(swatchX, top - 0.5, 4, 4, 'S');
  hatch(doc, swatchX, top - 0.5, 4, 4);
  doc.text('sem dado', swatchX + 5.5, top + 2.6);
}

function drawFooter(doc: JsPDF, page: number, pages: number, generatedAt: Date, size: {
  width: number; height: number;
}) {
  const y = size.height - MARGIN + 2;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SIZE);
  doc.setTextColor(...GRAY_TEXT);
  doc.text(`Gerado em ${format(generatedAt, "dd/MM/yyyy 'às' HH:mm")}`, MARGIN, y);
  doc.text(`Página ${page} de ${pages}`, size.width - MARGIN, y, { align: 'right' });
}

export function renderHeatmapPdf(
  JsPDF: JsPDFConstructor,
  autoTable: typeof AutoTableFn,
  matrix: HeatmapMatrix,
  generatedAt: Date,
): JsPDF {
  const doc = new JsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const table = buildHeatmapPdfTable(matrix);

  // Coluna das cidades do tamanho do maior nome; os dias dividem o resto da largura,
  // sem ficar estreitos demais (abaixo disso, o autoTable leva os dias para outra página).
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT_SIZE);
  const labelWidths = matrix.rows.map((r) => doc.getTextWidth(r) + 2 * CELL_PADDING + 1);
  const labelWidth = Math.min(55, Math.max(25, ...labelWidths));
  // 1 mm de folga: a soma exata das colunas estoura por arredondamento e o autoTable
  // mandava o último dia sozinho para outra página.
  const available = width - 2 * MARGIN - labelWidth - 1;
  // A coluna nunca fica mais estreita que a maior palavra do cabeçalho ("13/09/26"): o
  // cabeçalho quebra na hora ("13/09/26" / "00h"), mas uma palavra maior que a coluna era
  // cortada (saía só "2026-").
  const widestDateWord = Math.max(
    0,
    ...matrix.dates.flatMap((d) => d.split(/\s+/)).map((word) => doc.getTextWidth(word)),
  );
  // Limitada ao espaço útil: um rótulo muito longo não gera coluna maior que a página.
  const minDateColumn = Math.min(
    available,
    Math.max(MIN_DATE_COLUMN, widestDateWord + 2 * CELL_PADDING + 1),
  );
  const dateWidth = Math.max(
    minDateColumn,
    Math.min(MAX_DATE_COLUMN, available / matrix.dates.length),
  );

  autoTable(doc, {
    head: table.head,
    body: table.body,
    startY: HEADER_HEIGHT + MARGIN / 2,
    margin: {
      top: HEADER_HEIGHT + MARGIN / 2, bottom: FOOTER_HEIGHT, left: MARGIN, right: MARGIN,
    },
    theme: 'grid',
    horizontalPageBreak: true,
    horizontalPageBreakRepeat: 0,
    // Rótulo de várias linhas deixa a linha alta: sem 'avoid' o autoTable a parte na fronteira
    // da página e a sobra (valores vazios) vai para a página seguinte.
    rowPageBreak: 'avoid',
    styles: {
      font: 'helvetica',
      fontSize: FONT_SIZE,
      cellPadding: CELL_PADDING,
      halign: 'center',
      valign: 'middle',
      lineColor: WHITE,
      lineWidth: 0.2,
      cellWidth: dateWidth,
      overflow: 'hidden',
    },
    headStyles: {
      fillColor: DARK_BLUE, textColor: WHITE, fontStyle: 'bold', overflow: 'linebreak',
    },
    columnStyles: {
      0: {
        cellWidth: labelWidth,
        halign: 'left',
        fontStyle: 'bold',
        textColor: MEDIUM_BLUE,
        fillColor: WHITE,
        // Rótulo acima do teto de 55 mm quebra em linhas; com 'hidden' saía cortado sem aviso.
        overflow: 'linebreak',
      },
    },
    // row.index < 0: sobra de uma linha que o autoTable partiu (só se ela for mais alta que a
    // página, mesmo com 'avoid'); não é uma célula da matriz e nunca vira "sem dado".
    didParseCell: (data: CellHookData) => {
      if (data.section !== 'body' || data.column.index === 0 || data.row.index < 0) return;
      const style = table.cellStyle(data.row.index, data.column.index - 1);
      if (style.noData) {
        Object.assign(data.cell.styles, { fillColor: WHITE });
      } else {
        Object.assign(data.cell.styles, { fillColor: style.fill, textColor: style.text });
      }
    },
    didDrawCell: (data: CellHookData) => {
      if (data.section !== 'body' || data.column.index === 0 || data.row.index < 0) return;
      const style = table.cellStyle(data.row.index, data.column.index - 1);
      if (style.noData) hatch(doc, data.cell.x, data.cell.y, data.cell.width, data.cell.height);
    },
  });

  // Cabeçalho, legenda e rodapé em TODAS as páginas, depois da tabela: assim o
  // "Página X de Y" já sabe o total, e nenhuma página fica sem a escala de cores.
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    drawHeader(doc, matrix, width);
    drawLegend(doc, matrix, height);
    drawFooter(doc, page, pages, generatedAt, { width, height });
  }

  return doc;
}

/** Gera e baixa o PDF. As bibliotecas só são carregadas no clique (fora do bundle inicial). */
export default async function downloadHeatmapPdf(
  matrix: HeatmapMatrix,
  filename: string,
  generatedAt = new Date(),
): Promise<void> {
  const [{ jsPDF }, { autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  renderHeatmapPdf(jsPDF, autoTable, matrix, generatedAt).save(filename);
}
